/**
 * Builds the cache key that scopes readiness evidence to the setup revision it
 * was actually observed under.
 *
 * Family satisfaction is expensive to re-measure, so the readiness hook caches
 * the last verified result against this key. Without the verified-revision
 * token in the key, evidence gathered before a connector target was replaced or
 * a profile was re-read would keep answering for the revision that followed it.
 */
export interface MappingReadinessRevisionScopeInput {
  /** Signed-in user; evidence is never shared across accounts. */
  userId: string | null;
  /** Active Revit project, or `null` when no document is open. */
  projectKey: string | null;
  /** Active profile, or an empty string when the setup is unscoped. */
  profileKey: string;
  /** Requirement keys the evidence was measured against. */
  requirementKeys: readonly string[];
  /**
   * Opaque token naming the currently verified setup revision, or `null` while
   * a revision is open. `null` is a scope of its own: unverified evidence must
   * never be reused once a revision settles.
   */
  verifiedRevision: string | null;
}

const normalizeUserId = (userId: string | null): string | null => {
  const normalized = userId?.trim().toLowerCase();
  return normalized ? normalized : null;
};

const normalizeRequirementKeys = (requirementKeys: readonly string[]): string[] =>
  [...new Set(requirementKeys)].sort();

export function buildMappingReadinessRevisionScopeKey(
  input: MappingReadinessRevisionScopeInput
): string {
  return JSON.stringify({
    userId: normalizeUserId(input.userId),
    projectKey: input.projectKey,
    profileKey: input.profileKey,
    requirements: normalizeRequirementKeys(input.requirementKeys),
    revision: input.verifiedRevision,
  });
}

export function buildMappingRepairAttemptScopeKey(
  input: MappingReadinessRevisionScopeInput
): string {
  return JSON.stringify({
    userId: normalizeUserId(input.userId),
    projectKey: input.projectKey,
    profileKey: input.profileKey,
    requirements: normalizeRequirementKeys(input.requirementKeys),
  });
}
